import { Children, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { MCButton } from "../../MainComponents/Button/Button";
import _ from "lodash";
import { ModalSegmentCatalogue } from "./ModalSegmentCatalogue";
import {
  createGroupEAM,
  getGroupEAM,
  getUtilitiesEAM,
  updateGroupEAM,
  updateUserEAM,
} from "../../../apis/apiEAM";
import { MCInput } from "../../MainComponents/Input/Input";
import { MCLabel } from "../../MainComponents/Label/Label";
import { useLocation, useNavigate, useParams } from "react-router";
import { catalogByPart } from "../../../apis";
import { GeneralIconHeader } from "../../Title/GeneralHeader";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { Breadcrum } from "../../MainComponents/Breadcrumb/Breadcrumb";
import { Box, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import Icon from "../../Icon/Icon";
import { EGIconButton } from "../../MainComponents/Button/IconButton/IconButton";

const localizeOtherLabel = (label, t) => {
  if (typeof label !== "string") return label;
  const normalized = label.toLowerCase().trim();
  if (normalized === "other" || normalized === "otro") {
    return t("Segmentation.other");
  }
  return label;
};

// Custom TreeView components
const CustomTreeView = ({ children, ...props }) => (
  <ul className="custom-tree-view" role="tree" {...props}>
    {children}
  </ul>
);

const CustomTreeItem = ({ itemId, label, children }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = Children.toArray(children).some(Boolean);

  return (
    <li className="custom-tree-item" role="treeitem">
      <div className="custom-tree-item-content">
        {hasChildren ? (
          <span
            className="custom-tree-item-toggle"
            onClick={() => setExpanded(!expanded)}
          >
            <Icon
              name={expanded ? "arrow_down_2" : "outline_arrow_right_4"}
              size={16}
            />
          </span>
        ) : (
          <span className="custom-tree-item-spacer" />
        )}
        <div
          className="custom-tree-item-label"
          onClick={hasChildren ? () => setExpanded(!expanded) : undefined}
          style={{ cursor: hasChildren ? "pointer" : "default" }}
        >
          {label}
        </div>
      </div>
      {hasChildren && expanded && (
        <ul className="custom-tree-item-children" role="group">
          {children}
        </ul>
      )}
    </li>
  );
};

const ListItemValues = ({ path, field }) => {
  const [label, setLabel] = useState(null);
  const [t] = useTranslation("users");

  useEffect(() => {
    const splitedField = field.split("::");
    const splitedPath = path.split("::");
    const value = splitedPath.splice(-2);
    const joined = splitedPath.join("::");
    catalogByPart({
      catalogue: splitedField[2],
      path: joined === "" ? "/" : joined,
      is_own: splitedField[1] === "OWN" ? true : false,
    }).then((resp) => {
      const labelTemp = resp.data.find((item) => item.key === value[1]).label;
      setLabel(localizeOtherLabel(labelTemp, t));
    });
  }, [path]);

  return <li className="list-values-value">{label ? label : "..."}</li>;
};

const SegmentChild = ({
  scope,
  path,
  handleAddChild,
  handleEditValues,
  handleDeleteChild,
  utilitiesCatalogues,
  showAllFilters,
}) => {
  const [showModalSegment, setShowModalSegment] = useState(false);
  const [showModalSegmentEdit, setShowModalSegmentEdit] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [t] = useTranslation("users");

  useEffect(() => {
    setShowFilters(showAllFilters);
  }, [showAllFilters]);

  const CustomLabel = (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 0.5,
          pr: 0,
          width: "100%",
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: "inherit", flexGrow: 1 }}>
          {localizeOtherLabel(scope.label, t)}
        </Typography>

        <Box sx={{ display: "flex", gap: 0.5 }}>
          <EGIconButton
            iconName="more_dots"
            size="small"
            iconColor="primary"
            title={t("Segmentation.edit")}
            placement="bottom"
            onClick={(e) => {
              e.stopPropagation();
              setShowModalSegmentEdit(true);
            }}
          />

          <EGIconButton
            iconName="add_icon"
            size="small"
            iconColor="green"
            title={t("Segmentation.add_segment")}
            placement="bottom"
            onClick={(e) => {
              e.stopPropagation();
              setShowModalSegment(true);
            }}
          />
          <EGIconButton
            iconName="trash_can"
            size="small"
            iconColor="error"
            title={t("Segmentation.delete")}
            placement="bottom"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteChild(path);
            }}
          />
          <EGIconButton
            iconName={showFilters ? "outline_eye_slash" : "outline_eye"}
            size="small"
            iconColor="green"
            title={
              showFilters
                ? t("Segmentation.hide_filters")
                : t("Segmentation.view_filters")
            }
            placement="bottom"
            onClick={(e) => {
              e.stopPropagation();
              setShowFilters(!showFilters);
            }}
          />
        </Box>
      </Box>
      <Divider variant="middle" />
    </Box>
  );

  return (
    <>
      {showModalSegment && (
        <ModalSegmentCatalogue
          target="add"
          scope={scope}
          path={path}
          show={showModalSegment}
          onHide={() => setShowModalSegment(false)}
          handleAddChild={handleAddChild}
          utilitiesCatalogues={utilitiesCatalogues}
        />
      )}
      {showModalSegmentEdit && (
        <ModalSegmentCatalogue
          target="edit"
          scope={scope}
          path={path}
          show={showModalSegmentEdit}
          onHide={() => setShowModalSegmentEdit(false)}
          handleAddChild={handleAddChild}
          handleEditValues={handleEditValues}
          utilitiesCatalogues={utilitiesCatalogues}
        />
      )}

      <CustomTreeItem itemId={path} label={CustomLabel}>
        {showFilters && Array.isArray(scope?.value) && (
          <Box sx={{ pl: 2, py: 1 }}>
            <ul className="list-values mb-0">
              {scope.value.map((item, idx) => (
                <ListItemValues key={idx} path={item} field={scope.field} />
              ))}
            </ul>
          </Box>
        )}

        {Array.isArray(scope.child) &&
          scope.child.map((item, idx) => (
            <SegmentChild
              key={idx}
              scope={item}
              path={`${path}.child.${idx}`}
              handleAddChild={handleAddChild}
              handleEditValues={handleEditValues}
              handleDeleteChild={handleDeleteChild}
              utilitiesCatalogues={utilitiesCatalogues}
              showAllFilters={showAllFilters}
            />
          ))}
      </CustomTreeItem>
    </>
  );
};

export const Segmentation = () => {
  const { gTheme } = useSelector((state) => state.theme);
  const params = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [utilitiesCatalogues, setUtilitiesCatalogues] = useState(null);
  const [showModalSegment, setShowModalSegment] = useState(false);
  const [name, setName] = useState("");
  const [action, setAction] = useState([]);
  const [is_active, setIs_active] = useState(true);
  const [segment, setSegment] = useState([]);
  const isUser = state?.user || false;
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [t] = useTranslation("users");

  useEffect(() => {
    getUtilitiesEAM().then((utilities) => {
      setUtilitiesCatalogues(utilities.catalogues);
    });
    if (params?.id) {
      getGroupEAM(params.id).then((resp) => {
        setIs_active(resp.is_active);
        setAction(resp.action);
        setName(resp.name);
        const arrSegment = resp.segment ? Object.values(resp.segment) : [];
        setSegment(arrSegment);
      });
    } else if (isUser) {
      setName(state.user.username);
      const arrSegment = state.user.segment
        ? Object.values(state.user.segment)
        : [];
      setSegment(arrSegment);
    }
  }, []);

  const handleAddChild = (path, child) => {
    const newSegment = _.cloneDeep(segment);
    let arrToAdd;
    if (path === "") {
      arrToAdd = _.cloneDeep(segment);
      if (!Array.isArray(arrToAdd)) {
        arrToAdd = [];
      }
    } else {
      const arrTemp = _.get(newSegment, `${path}.child`);
      arrToAdd = Array.isArray(arrTemp) ? arrTemp : [];
    }
    arrToAdd.push(child);
    if (path === "") {
      setSegment(arrToAdd);
    } else {
      _.set(newSegment, `${path}.child`, arrToAdd);
      setSegment(newSegment);
    }
  };

  const handleEditValues = (path, edited) => {
    const newSegment = _.cloneDeep(segment);
    _.set(newSegment, `${path}`, edited);
    setSegment(newSegment);
  };

  const handleDeleteChild = (path) => {
    const newSegment = _.cloneDeep(segment);
    const splitedPath = path.split(".");
    let idx = splitedPath.pop();
    let arrToDel;
    if (splitedPath.length === 0) {
      arrToDel = newSegment;
    } else {
      arrToDel = _.get(newSegment, splitedPath.join("."));
    }
    arrToDel.splice(idx, 1);
    arrToDel = arrToDel.length > 0 ? arrToDel : null;
    if (splitedPath.length === 0) {
      setSegment(arrToDel);
    } else {
      _.set(newSegment, splitedPath.join("."), arrToDel);
      setSegment(newSegment);
    }
  };

  // Convierte array a objeto y elimina _id (solo para uso interno de React)
  const prepareSegmentForSave = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const obj = {};
    arr.forEach((item, idx) => {
      const { _id, ...rest } = item;
      obj[idx] = {
        ...rest,
        child: prepareSegmentForSave(item.child),
      };
    });
    return obj;
  };

  const handleCreateSegment = async () => {
    if (name.trim() === "") {
      Swal.fire({
        title: t("Segmentation.name_empty"),
        icon: "error",
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: "Ok",
      });
      return;
    }
    await createGroupEAM({ name, action, segment, is_active });
    navigate(-1);
  };

  const handleUpdateSegment = async () => {
    await updateGroupEAM(params.id, { name, action, segment, is_active });
    navigate(-1);
  };

  const handleSegmentUser = async () => {
    const resp = await updateUserEAM({
      user_id: state.user.id,
      segment: segment ? segment : null,
    });
    if (!resp?.error) {
      navigate(-1);
    } else {
      console.error(resp);
    }
  };

  const breadcrumbItems = [
    { label: t("Segmentation.home"), route: "/" },
    { label: t("Segmentation.users"), route: "/users" },
    { label: t("Segmentation.groups"), route: "/users/segments" },
    {
      label: `${
        params.id ? t("Segmentation.edit") : t("Segmentation.create")
      } ${
        isUser ? t("Segmentation.user_filter") : t("Segmentation.group_segment")
      }`,
    },
  ];

  return (
    <div className={`segments-page ${gTheme !== "light" ? "dark-mode" : ""}`}>
      {showModalSegment && (
        <ModalSegmentCatalogue
          target="add"
          path={""}
          scope={segment}
          show={showModalSegment}
          onHide={() => setShowModalSegment(false)}
          handleAddChild={handleAddChild}
          utilitiesCatalogues={utilitiesCatalogues}
        />
      )}

      <GeneralIconHeader
        title={`${
          params.id ? t("Segmentation.edit") : t("Segmentation.create")
        } ${
          isUser
            ? t("Segmentation.user_filter")
            : t("Segmentation.group_segment")
        }`}
        text={`${
          isUser
            ? t("Segmentation.configures_reports_which_user_access")
            : t("Segmentation.assigns_set_reports_which_group_access")
        }`}
        icon={isUser ? "outline_profile_tick" : "outline_people"}
      />

      <Breadcrum items={breadcrumbItems} />

      <div className="dyTheme1 dyBorder1 rounded p-20 mt-20">
        <div className="mb-3">
          {isUser ? (
            <MCLabel text={`${t("Segmentation.username")}:`} />
          ) : (
            <MCLabel text={`${t("Segmentation.group_name")}:`} />
          )}

          <div className="d-flex align-items-center">
            <MCInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isUser}
            />
          </div>
        </div>

        <div className="children-container">
          <CustomTreeView aria-label="segmentation tree">
            {Array.isArray(segment) &&
              segment.map((item, idx) => (
                <SegmentChild
                  key={idx}
                  scope={item}
                  path={`${idx}`}
                  handleAddChild={handleAddChild}
                  handleDeleteChild={handleDeleteChild}
                  handleEditValues={handleEditValues}
                  utilitiesCatalogues={utilitiesCatalogues}
                  showAllFilters={showAllFilters}
                />
              ))}
          </CustomTreeView>
        </div>

        <MCButton
          label={t("Segmentation.add_segment")}
          variant="primary"
          outline
          className="mt-3 me-2"
          onClick={() => setShowModalSegment(true)}
        />
        {params.id ? (
          <MCButton
            label={t("Segmentation.save")}
            variant="success"
            outline
            className="mt-3"
            onClick={handleUpdateSegment}
          />
        ) : state?.user ? (
          <MCButton
            label={t("Segmentation.save")}
            variant="success"
            outline
            className="mt-3"
            onClick={handleSegmentUser}
          />
        ) : (
          <MCButton
            label={t("Segmentation.create")}
            variant="success"
            outline
            className="mt-3"
            onClick={handleCreateSegment}
          />
        )}
      </div>
    </div>
  );
};
